import {
  getToolName,
  isStaticToolUIPart,
  type ToolUIPart,
  type UIMessage,
} from "ai"
import {
  AlertCircle,
  BookOpen,
  CheckCircle2,
  Loader2,
  PencilLine,
} from "lucide-react"

import { OnboardingCompleteCard } from "@/components/onboarding-complete-card"
import { PruefungsfrageCard } from "@/components/pruefungsfrage-card"
import { PruefungsstandCard } from "@/components/pruefungsstand-card"
import { TrainingSuggestionCard } from "@/components/training-suggestion-card"

/** Tools whose result is a card under the message, not a pill above it. */
const KARTEN = new Set([
  "trainingVorschlagen",
  "naechsteFrage",
  "lernstandZeigen",
])

/**
 * Wie ein Werkzeug im Gespräch benannt wird.
 *
 * Je Werkzeug eigene Verben statt einer Vorlage: „Produktunterlagen
 * nachgeschlagen" ist richtig, „selbstbildErfassen nachgeschlagen" war der
 * technische Name in der Sprache eines Nachschlagevorgangs.
 *
 * `zeigen: false` blendet reine Mechanik aus. Dass der Coach den Übergang zur
 * Analyse beantragt hat, ist für die Beraterin keine Auskunft — sie sieht am
 * Gespräch, ob er ihn bekommen hat.
 */
const WERKZEUGE: Record<
  string,
  {
    laeuft: string
    fertig: string
    fehler: string
    icon: "buch" | "stift"
    zeigen?: boolean
  }
> = {
  lookupProductKnowledge: {
    laeuft: "Produktunterlagen werden durchsucht",
    fertig: "Produktunterlagen nachgeschlagen",
    fehler: "Produktunterlagen nicht erreichbar",
    icon: "buch",
  },
  selbstbildErfassen: {
    laeuft: "Notiert, was du gesagt hast",
    fertig: "Notiert, was du gesagt hast",
    fehler: "Konnte deine Sicht nicht festhalten",
    icon: "stift",
  },
  skillLaden: {
    laeuft: "",
    fertig: "",
    fehler: "",
    icon: "buch",
    zeigen: false,
  },
  analyseFreigeben: {
    laeuft: "",
    fertig: "",
    fehler: "",
    icon: "buch",
    zeigen: false,
  },
  feedbackFesthalten: {
    laeuft: "",
    fertig: "",
    fehler: "",
    icon: "stift",
    zeigen: false,
  },
  essenzErfassen: {
    laeuft: "Notiert, was du gesagt hast",
    fertig: "Notiert, was du gesagt hast",
    fehler: "Konnte deine Antwort nicht festhalten",
    icon: "stift",
  },
  // Der Antrag selbst bleibt Mechanik; der GEWÄHRTE Abschluss erscheint als
  // Karte mit den nächsten Schritten (siehe isCard).
  essenzAbschliessen: {
    laeuft: "",
    fertig: "",
    fehler: "",
    icon: "buch",
    zeigen: false,
  },
  pruefungswissenNachschlagen: {
    laeuft: "",
    fertig: "",
    fehler: "",
    icon: "buch",
    zeigen: false,
  },
  merken: {
    laeuft: "",
    fertig: "",
    fehler: "",
    icon: "stift",
    zeigen: false,
  },
}

/**
 * Als Pille über der Antwort erscheint nur, was hier benannt ist und gezeigt
 * werden soll — ein unbekannter Werkzeugname verrät Mechanik, ohne etwas zu
 * erklären. Karten haben ihren eigenen Platz unter der Antwort.
 */
function isPill(part: ToolUIPart) {
  const name = getToolName(part)
  return (
    !KARTEN.has(name) && WERKZEUGE[name]?.zeigen !== false && !!WERKZEUGE[name]
  )
}
function isCard(part: ToolUIPart) {
  const name = getToolName(part)
  // Nur der gewährte Abschluss wird zur Karte: eine Ablehnung ist Stoff für
  // den Coach im Gespräch, kein Klickziel — und während der Antrag läuft,
  // gibt es nichts zu zeigen.
  if (name === "essenzAbschliessen") {
    return (
      part.state === "output-available" &&
      (part.output as { abgeschlossen?: boolean })?.abgeschlossen === true
    )
  }
  return KARTEN.has(name)
}

const BEREICH_LABELS: Record<string, string> = {
  fakten: "Leistungen und Bedingungen",
  zielmarkt: "Zielmarkt",
  ansprachen: "Ansprachen",
  einwaende: "Einwände",
}

function gegenstand(part: ToolUIPart): string | undefined {
  const input = part.input as { bereich?: string } | undefined
  if (!input?.bereich) return undefined
  return BEREICH_LABELS[input.bereich] ?? input.bereich
}

function ToolPill({ part }: { part: ToolUIPart }) {
  const werkzeug = WERKZEUGE[getToolName(part)]
  const laeuft =
    part.state === "input-streaming" || part.state === "input-available"
  const fehler = part.state === "output-error"
  const thema = gegenstand(part)

  // Ein unbekanntes Werkzeug bleibt unsichtbar: der technische Name im
  // Gespräch verrät die Mechanik, ohne etwas zu erklären.
  if (!werkzeug || werkzeug.zeigen === false) return null

  const text = fehler
    ? werkzeug.fehler
    : laeuft
      ? werkzeug.laeuft
      : werkzeug.fertig

  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary/20 bg-primary/[0.05] py-1 pr-3 pl-2.5 text-[13px] text-primary">
      {fehler ? (
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
      ) : laeuft ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
      ) : (
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
      )}
      {werkzeug.icon === "stift" ? (
        <PencilLine className="h-3.5 w-3.5 shrink-0 opacity-70" />
      ) : (
        <BookOpen className="h-3.5 w-3.5 shrink-0 opacity-70" />
      )}
      <span className="truncate">{text}</span>
      {thema && !laeuft && !fehler && (
        <span className="truncate border-b border-primary/40 font-medium">
          {thema}
        </span>
      )}
    </span>
  )
}

/**
 * What the coach looked up before answering. Shown because the product's claim
 * is "belegt statt geraten": an answer whose source stays invisible is worth as
 * much as one without a source.
 */
export function hasToolCalls(message: UIMessage): boolean {
  return message.parts.some((p) => isStaticToolUIPart(p) && isPill(p))
}

export function CoachToolCalls({ message }: { message: UIMessage }) {
  const parts = message.parts.filter(isStaticToolUIPart).filter(isPill)
  if (!parts.length) return null

  return (
    <div className="mb-2.5 flex flex-wrap gap-1.5">
      {parts.map((part) => (
        <ToolPill key={part.toolCallId} part={part} />
      ))}
    </div>
  )
}

/** The coach agreed with her to write the feedback down; the page starts it. */
export function hatFeedbackFestgehalten(message: UIMessage): boolean {
  return message.parts.some(
    (p) =>
      isStaticToolUIPart(p) &&
      getToolName(p) === "feedbackFesthalten" &&
      p.state === "output-available"
  )
}

/** What the coach proposes with the answer, rendered as cards under it. */
export function hasToolCards(message: UIMessage): boolean {
  return message.parts.some((p) => isStaticToolUIPart(p) && isCard(p))
}

export function CoachToolCards({ message }: { message: UIMessage }) {
  const parts = message.parts.filter(isStaticToolUIPart).filter(isCard)
  if (!parts.length) return null

  return (
    <div className="space-y-2">
      {parts.map((part) => {
        switch (getToolName(part)) {
          case "essenzAbschliessen":
            return <OnboardingCompleteCard key={part.toolCallId} part={part} />
          case "naechsteFrage":
            return <PruefungsfrageCard key={part.toolCallId} part={part} />
          case "lernstandZeigen":
            return <PruefungsstandCard key={part.toolCallId} part={part} />
          default:
            return <TrainingSuggestionCard key={part.toolCallId} part={part} />
        }
      })}
    </div>
  )
}
